import React, { useState, useEffect } from 'react';
import NavbarRolCartera from '../navbarRolCartera';
import { message, Button, Modal, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { utils, writeFile } from 'xlsx';
import './transferenciasRolCartera.css';

const TransferenciasRolCartera = () => {
  const [bodegas, setBodegas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalEnviaVisible, setIsModalEnviaVisible] = useState(false);
  const [isModalRecibeVisible, setIsModalRecibeVisible] = useState(false);
  const [selectedBranchDetails, setSelectedBranchDetails] = useState(null);
  
  // New state variables for custom table
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'fechaTransferencia', direction: 'descending' });

  useEffect(() => {
    fetchBodegas();
  }, []);

  const fetchBodegas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://99envios.app/api/online/transferencias', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status !== 200) {
        throw new Error(`Error HTTP: ${response.status}`);
      }
      const data = response.data.transferencias || [];
      const transferenciasFormateadas = data.map((transfer, index) => ({
        key: (index + 1).toString(),
        id: transfer.id,
        sucursal_origen: transfer.sucursal_origen,
        sucursal_destino: transfer.sucursal_destino,
        monto: transfer.monto,
        descripcion: transfer.descripcion,
        fechaTransferencia: transfer.fecha_transferencia,
      }))
      // Ordenar por fecha más reciente primero
      .sort((a, b) => new Date(b.fechaTransferencia) - new Date(a.fechaTransferencia));
      setBodegas(transferenciasFormateadas);
    } catch (error) {
      console.error('Error al obtener las transferencias:', error);
      message.error('No se pudieron cargar las transferencias');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const worksheet = utils.json_to_sheet(bodegas);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Transferencias");
    writeFile(workbook, "transferencias.xlsx");
  };

  const showBranchDetails = (type, record) => {
    let branchDetails = null;
    if (type === 'envia') {
      branchDetails = record.sucursal_origen;
      setIsModalEnviaVisible(true);
    } else {
      branchDetails = record.sucursal_destino;
      setIsModalRecibeVisible(true);
    }
    setSelectedBranchDetails(branchDetails);
  };

  // New functions for custom table
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      if (a[sortConfig.key] === null) return 1;
      if (b[sortConfig.key] === null) return -1;
      
      if (sortConfig.key === 'fechaTransferencia') {
        const dateA = new Date(a[sortConfig.key]);
        const dateB = new Date(b[sortConfig.key]);
        return sortConfig.direction === 'ascending' ? dateA - dateB : dateB - dateA;
      }
      
      if (typeof a[sortConfig.key] === 'string') {
        if (sortConfig.direction === 'ascending') {
          return a[sortConfig.key].localeCompare(b[sortConfig.key]);
        } else {
          return b[sortConfig.key].localeCompare(a[sortConfig.key]);
        }
      } else {
        if (sortConfig.direction === 'ascending') {
          return a[sortConfig.key] - b[sortConfig.key];
        } else {
          return b[sortConfig.key] - a[sortConfig.key];
        }
      }
    });
  };

  const filterData = (data) => {
    if (!searchTerm) return data;
    
    return data.filter(item => 
      Object.values(item).some(val => 
        val && val.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  // Get filtered and sorted data
  const filteredAndSortedData = sortData(filterData(bodegas));
  
  // Calculate pagination values
  const indexOfLastItem = currentPage * entriesPerPage;
  const indexOfFirstItem = indexOfLastItem - entriesPerPage;
  const currentItems = filteredAndSortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAndSortedData.length / entriesPerPage);
  
  // Function to change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format monto as currency
  const getFormattedMonto = (monto) => {
    return parseFloat(monto).toLocaleString('es-CO', {
      style: 'currency',
      currency: 'COP',
    });
  };

  return (
    <>
      <NavbarRolCartera title="Transferencias"/>
      <div className="transferencias-container">
        <div className="transferencias-actions">
          <Button 
            onClick={exportToExcel}
            className="transferencias-button transferencias-export-button"
          >
            Exportar a Excel
          </Button>
        </div>
        
        <div className="transferencias-search">
          <Input
            placeholder="Buscar en todos los campos"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            prefix={<SearchOutlined />}
            className="transferencias-search-input"
          />
        </div>
        
        {loading ? (
          <div className="transferencias-loading">Cargando datos...</div>
        ) : (
          <div className="transferencias-table-wrapper">
            <table className="transferencias-custom-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('id')}>
                    ID {sortConfig.key === 'id' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('sucursal_origen')}>
                    Sucursal Origen {sortConfig.key === 'sucursal_origen' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('sucursal_destino')}>
                    Sucursal Destino {sortConfig.key === 'sucursal_destino' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('monto')}>
                    Monto {sortConfig.key === 'monto' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('descripcion')}>
                    Descripción {sortConfig.key === 'descripcion' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('fechaTransferencia')}>
                    Fecha Transferencia {sortConfig.key === 'fechaTransferencia' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => (
                  <tr key={item.key}>
                    <td>{item.id}</td>
                    <td>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                        <span>{item.envia}</span>
                        <span 
                          onClick={() => showBranchDetails('envia', item)}
                          className="ver-texto-transferencias"
                        >
                          Ver detalles
                        </span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                        <span>{item.recibe}</span>
                        <span 
                          onClick={() => showBranchDetails('recibe', item)}
                          className="ver-texto-transferencias"
                        >
                          Ver detalles
                        </span>
                      </div>
                    </td>
                    <td>{getFormattedMonto(item.monto)}</td>
                    <td>{item.descripcion}</td>
                    <td>{item.fechaTransferencia}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination controls */}
        {filteredAndSortedData.length > 0 && (
          <div className="pagination-container-transferencias">
            <div className="pagination-info-transferencias">
              Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, filteredAndSortedData.length)} de {filteredAndSortedData.length} registros
            </div>
            <div className="pagination-controls-transferencias">
              {/* First page button */}
              {currentPage > 2 && (
                <button 
                  onClick={() => paginate(1)} 
                  className="pagination-button-transferencias"
                >
                  1
                </button>
              )}
              
              {/* Previous button */}
              {currentPage > 1 && (
                <button 
                  onClick={() => paginate(currentPage - 1)} 
                  className="pagination-button-transferencias"
                >
                  &lt;
                </button>
              )}
              
              {/* Page numbers */}
              <div className="pagination-pages-transferencias">
                {(() => {
                  const pagesToShow = [];
                  
                  if (currentPage > 1) {
                    pagesToShow.push(currentPage - 1);
                  }
                  
                  pagesToShow.push(currentPage);
                  
                  if (currentPage < totalPages) {
                    pagesToShow.push(currentPage + 1);
                  }
                  
                  return pagesToShow.map(pageNum => (
                    <button 
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`pagination-button-transferencias ${currentPage === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  ));
                })()}
              </div>
              
              {/* Next button */}
              {currentPage < totalPages && (
                <button 
                  onClick={() => paginate(currentPage + 1)} 
                  className="pagination-button-transferencias"
                >
                  &gt;
                </button>
              )}
              
              {/* Last page button */}
              {currentPage < totalPages - 1 && (
                <button 
                  onClick={() => paginate(totalPages)} 
                  className="pagination-button-transferencias"
                >
                  {totalPages}
                </button>
              )}
            </div>
            <div className="items-per-page-transferencias">
              <span>Registros por página: </span>
              <select 
                value={entriesPerPage} 
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}
        
        <Modal
          title="Detalles de Sucursal Origen"
          open={isModalEnviaVisible}
          onCancel={() => setIsModalEnviaVisible(false)}
          footer={null}
        >
          {selectedBranchDetails && (
            <div>
              <p><strong>Código:</strong> {selectedBranchDetails.codigo}</p>
              <p><strong>Nombre:</strong> {selectedBranchDetails.nombre}</p>
              <p><strong>Correo:</strong> {selectedBranchDetails.correo}</p>
            </div>
          )}
        </Modal>

        <Modal
          title="Detalles de Sucursal Destino"
          open={isModalRecibeVisible}
          onCancel={() => setIsModalRecibeVisible(false)}
          footer={null}
        >
          {selectedBranchDetails && (
            <div>
              <p><strong>Código:</strong> {selectedBranchDetails.codigo}</p>
              <p><strong>Nombre:</strong> {selectedBranchDetails.nombre}</p>
              <p><strong>Correo:</strong> {selectedBranchDetails.correo}</p>
            </div>
          )}
        </Modal>
      </div>
    </>
  );
};

export default TransferenciasRolCartera;
